import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const whatsappNumber = '+966555150324';
  const whatsappLink = `https://wa.me/${whatsappNumber.replace('+', '')}`;
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // إنشاء رسالة واتساب
    const whatsappMessage = `
السلام عليكم، أنا ${formData.name}
رقم الجوال: ${formData.phone}
الخدمة المطلوبة: ${formData.service}
الرسالة: ${formData.message}
    `.trim();
    
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
    
    toast({
      title: "تم إرسال الطلب",
      description: "سيتم تحويلك إلى واتساب لإكمال الطلب",
    });
    
    // إعادة تعيين النموذج
    setFormData({ name: '', phone: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'اتصل بنا',
      content: whatsappNumber,
      link: `tel:${whatsappNumber}`,
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: MessageCircle,
      title: 'واتساب',
      content: 'تواصل فوري',
      link: whatsappLink,
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Mail,
      title: 'البريد الإلكتروني',
      content: 'info@housecraft-sa.com',
      link: 'mailto:info@housecraft-sa.com',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: MapPin,
      title: 'الموقع',
      content: 'طريق الملك عبدالله، بريدة',
      link: 'https://maps.google.com/?q=Buraidah,Qassim',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const workingHours = [
    { day: 'السبت - الخميس', hours: '8:00 صباحاً - 12:00 منتصف الليل' },
    { day: 'الجمعة', hours: '2:00 ظهراً - 12:00 منتصف الليل' },
    { day: 'الطوارئ', hours: '24 ساعة - 7 أيام' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" dir="rtl">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <a href="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_trusted-handyman-1/artifacts/bwyuu9dw_IMG_0972.jpeg" 
                  alt="هاوس كرافت - خدمات صيانة بريدة" 
                  className="h-12 w-auto"
                />
              </a>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="/" className="text-slate-600 hover:text-cyan-600 font-semibold transition-colors">الرئيسية</a>
              <a href="/services" className="text-slate-600 hover:text-cyan-600 font-semibold transition-colors">الخدمات</a>
              <a href="/about" className="text-slate-600 hover:text-cyan-600 font-semibold transition-colors">من نحن</a>
              <a href="/contact" className="text-cyan-600 font-semibold">اتصل بنا</a>
            </nav>
            <Button 
              onClick={() => window.open(whatsappLink, '_blank')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4 ml-2" />
              احجز الآن
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-white to-slate-50 opacity-60"></div>
        <div className="container mx-auto relative z-10 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            تواصل <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-cyan-500">معنا</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            نحن هنا لخدمتك. تواصل معنا الآن للحصول على استشارة مجانية أو لحجز خدمة صيانة منزلية في بريدة
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.link}
                target={info.link.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="block"
              >
                <Card className="text-center hover:shadow-2xl transition-all duration-300 border-2 hover:border-cyan-500 group h-full">
                  <CardContent className="p-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${info.color} rounded-2xl mb-4 group-hover:scale-110 transition-transform`}>
                      <info.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{info.title}</h3>
                    <p className="text-slate-600">{info.content}</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-slate-900 mb-4">أرسل طلبك</h2>
                <p className="text-lg text-slate-600">
                  املأ النموذج وسنتواصل معك في أقرب وقت ممكن
                </p>
              </div>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        الاسم الكامل *
                      </label>
                      <div className="relative">
                        <User className="absolute right-3 top-3 w-5 h-5 text-slate-400" />
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="pr-10 border-2 focus:border-cyan-500"
                          placeholder="أدخل اسمك"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        رقم الجوال *
                      </label>
                      <div className="relative">
                        <Phone className="absolute right-3 top-3 w-5 h-5 text-slate-400" />
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="pr-10 border-2 focus:border-cyan-500"
                          placeholder="05xxxxxxxx"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        نوع الخدمة *
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full p-3 border-2 rounded-lg focus:border-cyan-500 focus:outline-none bg-white"
                      >
                        <option value="">اختر الخدمة</option>
                        <option value="صيانة تكييف">صيانة تكييف</option>
                        <option value="خدمات كهرباء">خدمات كهرباء</option>
                        <option value="خدمات سباكة">خدمات سباكة</option>
                        <option value="صيانة دورية">صيانة دورية</option>
                        <option value="طوارئ">طوارئ</option>
                        <option value="أخرى">أخرى</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        تفاصيل الطلب
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute right-3 top-3 w-5 h-5 text-slate-400" />
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          className="pr-10 border-2 focus:border-cyan-500 resize-none"
                          placeholder="صف المشكلة أو الخدمة المطلوبة..."
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white font-bold text-lg py-6"
                    >
                      <Send className="w-5 h-5 ml-2" />
                      إرسال الطلب عبر واتساب
                    </Button>

                    <p className="text-sm text-slate-500 text-center">
                      سيتم تحويلك إلى واتساب لإكمال الطلب
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Map & Working Hours */}
            <div className="space-y-8">
              {/* Map */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">موقعنا</h3>
                <Card className="overflow-hidden border-2 border-slate-200">
                  <div className="h-80 bg-slate-100">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d227748.5937414344!2d43.66894805!3d26.326667599999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x157f7d1fb40a4b61%3A0x1e7e3a8c8c8c8c8c!2sBuraidah!5e0!3m2!1sen!2ssa!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      title="موقع هاوس كرافت في بريدة"
                    ></iframe>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1">العنوان</h4>
                        <p className="text-slate-600">طريق الملك عبدالله، بريدة، القصيم، المملكة العربية السعودية</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Working Hours */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">ساعات العمل</h3>
                <Card className="border-2 border-slate-200">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {workingHours.map((schedule, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 text-cyan-600" />
                            <span className="font-semibold text-slate-900">{schedule.day}</span>
                          </div>
                          <span className="text-slate-600">{schedule.hours}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 p-4 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg border border-cyan-200">
                      <p className="text-center text-cyan-900 font-semibold">
                        ⚡ خدمة الطوارئ متوفرة 24 ساعة يومياً
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-cyan-600 to-cyan-700">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            تحتاج مساعدة فورية؟
          </h2>
          <p className="text-xl text-cyan-50 mb-8 max-w-2xl mx-auto">
            تواصل معنا الآن عبر الواتساب أو الاتصال المباشر
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => window.open(whatsappLink, '_blank')}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xl font-bold py-7 px-10 rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              <MessageCircle className="w-6 h-6 ml-2" />
              واتساب: {whatsappNumber}
            </Button>
            <Button 
              size="lg"
              onClick={() => window.location.href = `tel:${whatsappNumber}`}
              variant="outline"
              className="bg-white text-cyan-600 border-2 border-white hover:bg-cyan-50 text-xl font-bold py-7 px-10 rounded-xl transition-all"
            >
              <Phone className="w-6 h-6 ml-2" />
              اتصل الآن
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-handyman-1/artifacts/bwyuu9dw_IMG_0972.jpeg" 
                alt="هاوس كرافت" 
                className="h-12 w-auto mb-4 brightness-200"
              />
              <p className="text-slate-400">
                شريكك الموثوق في الصيانة المنزلية ببريدة
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">روابط سريعة</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="/" className="hover:text-white transition-colors">الرئيسية</a></li>
                <li><a href="/services" className="hover:text-white transition-colors">الخدمات</a></li>
                <li><a href="/about" className="hover:text-white transition-colors">من نحن</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">اتصل بنا</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">خدماتنا</h3>
              <ul className="space-y-2 text-slate-400">
                <li>صيانة التكييف</li>
                <li>خدمات الكهرباء</li>
                <li>خدمات السباكة</li>
                <li>صيانة دورية</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">تواصل معنا</h3>
              <div className="space-y-3">
                <a href={`tel:${whatsappNumber}`} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  {whatsappNumber}
                </a>
                <div className="flex items-center gap-2 text-slate-400">
                  <MapPin className="w-5 h-5" />
                  <span>بريدة، القصيم</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p>© 2024 هاوس كرافت. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 z-50 animate-pulse"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
};

export default Contact;
